import { useState } from "react";
import { API_BASE_URL } from "../utils/constants.js";

export default function MicroTipApi(
    parameters = {},
    rows = [],
    calibrationResponse = {},
    dResponse = 0
) {
    const [response, setResponse] = useState(null);
    const [predictResponse, setPredictResponse] = useState(null);

    const [loading, setLoading] = useState(false);
    const [predictLoading, setPredictLoading] = useState(false);

    const [error, setError] = useState(null);
    const [predictError, setPredictError] = useState(null);

    // =========================
    // CALIBRATION PAYLOAD
    // =========================
    const buildPayload = () => {
        const cleanRows = (rows || []).filter(r => r.conc && r.resp);

        const afConcentration = [
            ...new Set(
                cleanRows
                    .map(r => Number(r.conc))
                    .filter(v => !isNaN(v))
            )
        ];

        const aadResponse = cleanRows.flatMap(r => {
            if (Array.isArray(r.resp)) {
                return r.resp.map(Number);
            }

            if (typeof r.resp === "string") {
                return r.resp
                    .split(/[\s,]+/)
                    .filter(v => v !== "")
                    .map(Number);
            }

            return [Number(r.resp)];
        });

        const repsMap = {};

        cleanRows.forEach(r => {
            const conc = Number(r.conc);

            const count = typeof r.resp === "string"
                ? r.resp.split(/[\s,]+/).filter(v => v !== "").length
                : Array.isArray(r.resp)
                    ? r.resp.length
                    : 1;

            repsMap[conc] = (repsMap[conc] || 0) + count;
        });

        const asNumberCalReps = afConcentration.map(
            c => repsMap[c] || 0
        );
       
        return {
            eWetCalModelType: Number(parameters?.TXBVersion || 0),
            sNumberCalLevels: afConcentration.length,
            afConcentration,
            aadResponse,
            asNumberCalReps,

            fReportableRangeMin:
                Number(parameters?.TXBReportableRangeMin || 0),

            fReportableRangeMax:
                Number(parameters?.TXBReportableRangeMax || 0),

            fWetCalInitialB0:
                Number(parameters?.TXBInitialB0 || 0),

            fWetCalFitB0Min:
                Number(parameters?.TXBMinB0 || 0),

            fWetCalFitB0Max:
                Number(parameters?.TXBMaxB0 || 0),

            fWetCalInitialB1:
                Number(parameters?.TXBInitialB1 || 0),

            fWetCalFitB1Min:
                Number(parameters?.TXBMinB1 || 0),

            fWetCalFitB1Max:
                Number(parameters?.TXBMaxB1 || 0),

            fWetCalInitialB2:
                Number(parameters?.TXBInitialB2 || 0),

            fWetCalFitB2Min:
                Number(parameters?.TXBMinB2 || 0),

            fWetCalFitB2Max:
                Number(parameters?.TXBMaxB2 || 0),

            fWetCalInitialB3:
                Number(parameters?.TXBInitialB3 || 0),

            fWetCalFitB3Min:
                Number(parameters?.TXBMinB3 || 0),

            fWetCalFitB3Max:
                Number(parameters?.TXBMaxB3 || 0),

            fWetCalInitialB4:
                Number(parameters?.TXBInitialB4 || 0),

            fWetCalFitB4Min:
                Number(parameters?.TXBMinB4 || 0),

            fWetCalFitB4Max:
                Number(parameters?.TXBMaxB4 || 0),

            fWetCalFitLambda:
                Number(parameters?.TXBLambda || 0),

            fWetCalFitEpsilon:
                Number(parameters?.TXBEpsilon || 0),

            fWetCalFitDelta:
                Number(parameters?.TXBDelta || 0),

            sWetCalNIterate:
                Number(parameters?.TXBNIterate || 0),

            fWetCalFitGoodnessLimit:
                Number(parameters?.TXBGoodnessLimit || 0),

            fWetCalFitMaxErrorLimit:
                Number(parameters?.TXBMaxErrorLimit || 0),

            fWetCalFitRelMaxErrorLimit:
                Number(parameters?.TXBMaxRelErrorLimit || 0),

            fWetCalFitMaxSD:
                Number(parameters?.TXBMaxSDLimit || 0),

            eAssayModelType: 20,

            bPerformCalQualityChecks:
                Number(parameters?.TXBPerformCalQualityChecks || 0),

            fAverageHighLevelAntigenExcessCheckValue:
                Number(parameters?.TXBAvgHighLevelAntigenExcessCheck || 0),

            fWetKineticProzoneLimit:
                Number(parameters?.TXBKineticProzoneLimit || 0),

            fWetMaxResponseHigh:
                Number(parameters?.TXBMaxResponseHigh || 0),

            fWetMaxResponseLow:
                Number(parameters?.TXBMaxResponseLow || 0),

            fWetMinResponseHigh:
                Number(parameters?.TXBMinResponseHigh || 0),

            fWetMinResponseLow:
                Number(parameters?.TXBMinResponseLow || 0),

            eMonotonicity:
                Number(parameters?.TXBMonotonicity || 0),

            sNumCalLevelDeltas: 0,

            fKExponent:
                Number(parameters?.TXBKExponent || 0),

            fWetBlankReadMultiplier:
                Number(parameters?.TXBWetBlankReadMultiplier || 0),

            fStandardTotalReagentVolume:
                Number(parameters?.TXBStdTotalReagentVolume || 0),

            sCalLevelToIgnore:
                Number(parameters?.TXBCalLevelToIgnore || 0),

            fExtremaCheck1:
                Number(parameters?.TXBExtremaCheck1 || 0),

            fExtremaCheck2:
                Number(parameters?.TXBExtremaCheck2 || 0),

            xG1Spline: {
                sSplineNodeCount: 0,
                afX: [],
                afY: [],
                afY2: []
            },

            xG2Spline: {
                sSplineNodeCount: 0,
                afX: [],
                afY: [],
                afY2: []
            }
        };
    };

    // =========================
    // PREDICTION PAYLOAD
    // =========================
    const buildPredictPayload = () => {
     
        return {
            dResponse: Number(dResponse),

            eWetCalModelType:
                Number(calibrationResponse?.eWetCalModelType || 0),

            fB0: Number(calibrationResponse?.B0 || 0),
            fB1: Number(calibrationResponse?.B1 || 0),
            fB2: Number(calibrationResponse?.B2 || 0),
            fB3: Number(calibrationResponse?.B3 || 0),
            fB4: Number(calibrationResponse?.B4 || 0),

            fR2: Number(calibrationResponse?.R2 || 0),

            fSplineX: calibrationResponse?.SplineX || [],
            fSplineY: calibrationResponse?.SplineY || [],
            fSplineY2: calibrationResponse?.SplineY2 || [],

            sNumberCalLevels:
                Number(calibrationResponse?.sNumberCalLevels || 0),

            fWetPredictConvergeCriteria:
                Number(
                    calibrationResponse?.fWetPredictConvergeCriteria || 0.0001
                ),

            fReportableRangeMax:
                Number(calibrationResponse?.fReportableRangeMax || 1000),

            fReportableRangeMin:
                Number(calibrationResponse?.fReportableRangeMin || 0),

            fCalInterceptA0:
                Number(calibrationResponse?.fCalInterceptA0 || 0),

            fCalSlopeA1:
                Number(calibrationResponse?.fCalSlopeA1 || 1),

            fCalCurvatureA2:
                Number(calibrationResponse?.fCalCurvatureA2 || 0),

            fCalBlankCorrectionFactorA3:
                Number(
                    calibrationResponse?.fCalBlankCorrectionFactorA3 || 0
                ),

            xG1Spline: calibrationResponse?.xG1Spline || {
                sSplineNodeCount: 0,
                afX: [],
                afY: [],
                afY2: []
            },

            xG2Spline: calibrationResponse?.xG2Spline || {
                sSplineNodeCount: 0,
                afX: [],
                afY: [],
                afY2: []
            },

            fKExponent:
                Number(calibrationResponse?.fKExponent || 0),

            eMonotonicityFlag:
                Number(calibrationResponse?.eMonotonicityFlag || 0)
        };
    };

    // =========================
    // CALIBRATION API
    // =========================
    const callApi = async () => {        
        const payload = buildPayload();

        console.log("CALIBRATION PAYLOAD:", payload);

        setLoading(true);
        setError(null);

        try {
            const res = await fetch(
                API_BASE_URL + "api/v1/microtip/calibrate",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify(payload)
                }
            );

            const data = await res.json();

            setResponse(data);

            return data;

        } catch (err) {
            setError(err.message);
            return null;

        } finally {
            setLoading(false);
        }
    };

    // =========================
    // PREDICTION API
    // =========================
    const callPredictApi = async (predictionInput = []) => {

        setPredictLoading(true);
        setPredictError(null);

        try {
            console.log("PREDICT ALL PAYLOAD:", predictionInput);
            console.log(" cal resp", calibrationResponse);
            const updatedResults = await Promise.all(

                predictionInput.map(async (item) => {

                    const payload = {
                        ...buildPredictPayload(),

                        dResponse: Number(item?.response || 0)
                    };

                    console.log("PREDICT PAYLOAD:", payload);

                    const res = await fetch(
                        API_BASE_URL + "api/v1/microtip/predict",
                        {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json"
                            },
                            body: JSON.stringify(payload)
                        }
                    );

                    const data = await res.json();

                    return {
                        ...item,

                        // update concentration from API response
                        concentration:
                            data?.dConcentration ?? "",

                        // optional full api response
                        apiResponse: data
                    };
                })
            );

            setPredictResponse(updatedResults);
            console.log("PREDICT response:", updatedResults);
            return updatedResults;

        } catch (err) {

            setPredictError(err.message);

            return null;

        } finally {

            setPredictLoading(false);
        }
    };

    return {
        // calibration
        callApi,
        response,
        loading,
        error,

        // prediction
        callPredictApi,
        predictResponse,
        predictLoading,
        predictError
    };
}
